/**
 * Admin → People (AIC-556): what the page derives from the server's roster (tracker-api people/store.ts), pure so the
 * flags, views, filters and wording are tested without a browser. The server sends facts; the rules Bill set live here:
 *
 *   role           only what Software Tracker saw on the person's own last sign-in (option C). Nothing here knows what the
 *                  IDC portal has granted.
 *   open           current cycle, Prepare through Review, owned plus covering (the server counts it).
 *   Overloaded     more than the server's `overload_limit` (30) open.
 *   Never signed in  an owner or staff member with open work and no sign-in. For the first 14 days of sign-in history it is
 *                  not flagged (cold start): the person shows "No sign-in since {date}" instead.
 *   Not found or left  every mapping that still needs an admin: left, a placeholder, ambiguous, not found, a suggestion, an
 *                  admin's unconfirm, a UPN another analyst has.
 *   Not linked to work  someone signed in as an analyst with no analysts row. A manager or an admin with no row owns no work
 *                  by design, so they are not flagged; nor is a sign-in whose role is not yet seen (migration 0062's backfill
 *                  made those for every past audit actor, managers and admins included), which reads "Role not yet seen".
 */
import { MAPPING_REQUEST_REASON } from "@swtracker/core/analyst-profile";
import type { PersonRow } from "../api/schemas.ts";

export type Source = "owner" | "staff" | "portal";
export type Flag = "cant" | "unlinked" | "gone" | "over";
export type Mapping = "confirmed" | "staff" | "left" | "ambiguous" | "placeholder" | "notfound" | "suggested" | "unconfirmed" | "upn_taken" | "none";
export type View = "flagged" | "all" | Flag;

export interface Ctx {
  /** Milliseconds since the epoch: the clock every relative time and the cold start read. */
  readonly now: number;
  /** When "Never signed in" starts to flag (14 days after sign-in history began); null with no history yet. */
  readonly flagFrom: number | null;
  /** When sign-in history began; null with none. */
  readonly startedAt: number | null;
  readonly limit: number;
  /** The signed-in admin's own address: their row reads "Now (you)". */
  readonly me: string;
}

const PLACEHOLDER = new Set(["", "N/A", "TBD"]);

export const nameOf = (r: PersonRow): string => r.owner_name ?? r.display_name ?? r.upn ?? "—";
export const keyOf = (r: PersonRow): string => r.id ?? `upn:${r.upn ?? ""}`;
export const sourceOf = (r: PersonRow): Source => (r.kind === "signed_in" ? "portal" : r.kind);
export const isPlaceholder = (r: PersonRow): boolean => r.kind === "owner" && r.owner_name !== null && PLACEHOLDER.has(r.owner_name.trim());

/** An owner with a confirmed UPN (a placeholder can be one): the mapping Unconfirm and Change sign-in email act on. */
export const isConfirmedOwner = (r: PersonRow): boolean => r.kind === "owner" && r.upn !== null && r.confirmed_by !== null && !r.left_at;

export function mappingOf(r: PersonRow): Mapping {
  if (r.kind === "signed_in") return "none";
  if (r.kind === "staff") return "staff";
  if (r.left_at && !r.confirmed_at) return "left";
  // A placeholder is never a person, confirmed or not: it keeps its warning.
  if (isPlaceholder(r)) return "placeholder";
  if (r.upn !== null && r.confirmed_by !== null) return "confirmed";
  if (r.unconfirmed_at) return "unconfirmed";
  if (((r.suggestion_detail ?? {}) as { upn_taken?: boolean }).upn_taken) return "upn_taken";
  if (r.suggestion_status === "suggested") return "suggested";
  if (r.suggestion_status === "ambiguous") return "ambiguous";
  return "notfound";
}

/** True while "Never signed in" is not yet flagged: the sign-in history is younger than 14 days (or has not started). */
export const warming = (ctx: Ctx): boolean => ctx.flagFrom === null || ctx.now < ctx.flagFrom;

export function flagOf(r: PersonRow, ctx: Ctx): Flag | null {
  const m = mappingOf(r);
  if (m !== "confirmed" && m !== "staff" && m !== "none") return "gone";
  if (r.work.open > 0 && r.upn && !r.last_seen_at && !warming(ctx)) return "cant";
  if (r.kind === "signed_in" && r.last_role === "analyst") return "unlinked";
  if (r.work.open > ctx.limit) return "over";
  return null;
}

export const FLAG_LABEL: Record<Flag, string> = { cant: "Never signed in", unlinked: "Not linked to work", gone: "Not found or left", over: "Overloaded" };
/** The flag's dot: risk for a person who can't see their work, the amber "yours to do" for the rest, off for someone who left. */
export const flagTone = (r: PersonRow, ctx: Ctx): "risk" | "you" | "off" => {
  const f = flagOf(r, ctx);
  return f === "cant" ? "risk" : f === "gone" && mappingOf(r) === "left" ? "off" : "you";
};
/** The row's flag in words: a mapping problem says which one. */
export function flagText(r: PersonRow, ctx: Ctx): string | null {
  const f = flagOf(r, ctx);
  if (f !== "gone") return f ? FLAG_LABEL[f] : null;
  return MAPPING_LABEL[mappingOf(r)];
}

export const MAPPING_LABEL: Record<Mapping, string> = {
  confirmed: "Confirmed", staff: "IDC staff", left: "Left IDC", ambiguous: "Multiple directory matches", placeholder: "Placeholder owner",
  notfound: "Not found in directory", suggested: "Suggested directory match", unconfirmed: "Unconfirmed by an admin", upn_taken: "UPN used by another analyst", none: "Not linked",
};

export const ROLE_LABEL = { analyst: "Analyst", manager: "Manager", admin: "Admin" } as const;
export const SOURCE_LABEL: Record<Source, string> = { owner: "Data Layer owner", staff: "IDC staff", portal: "Signed in, not linked" };
export const roleText = (r: PersonRow): string => (r.last_role ? ROLE_LABEL[r.last_role] : "Role not yet seen");

export const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;
/** The footer and announce: "98 people shown" when all are, "1 of 98 people shown" when filtered. */
export const countLine = (shown: number, total: number): string => (shown === total ? `${plural(total, "person", "people")} shown` : `${shown} of ${plural(total, "person", "people")} shown`);

/** "Just now", "12 minutes ago", "3 hours ago", "Yesterday", "5 days ago". */
export function relative(iso: string, now: number): string {
  const min = Math.max(0, (now - Date.parse(iso)) / 60_000);
  if (min < 10) return "Just now";
  if (min < 60) return `${Math.round(min)} minutes ago`;
  if (min < 24 * 60) return `${plural(Math.round(min / 60), "hour")} ago`;
  if (min < 48 * 60) return "Yesterday";
  return `${plural(Math.round(min / 1440), "day")} ago`;
}
export const fullDate = (iso: string): string => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
export const shortDate = (iso: string | number): string => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });

const isMe = (r: PersonRow, ctx: Ctx): boolean => r.upn !== null && r.upn.toLowerCase() === ctx.me.toLowerCase();
/** "Now (you)" for the caller's own row, else how long ago; null with no sign-in. */
export const lastText = (r: PersonRow, ctx: Ctx): string | null => (r.last_seen_at ? (isMe(r, ctx) ? "Now (you)" : relative(r.last_seen_at, ctx.now)) : null);
/** What a person with no sign-in shows: during the cold start only the date history began, after it "Never signed in". */
export const noSignIn = (ctx: Ctx): string => (!warming(ctx) ? "Never signed in" : ctx.startedAt === null ? "No sign-in yet" : `No sign-in since ${shortDate(ctx.startedAt)}`);

/** The phone row's second line (the flag already says "Never signed in", so a flagged owner's line is only their work). */
export function phoneLine(r: PersonRow, ctx: Ctx): string {
  const w = r.work;
  const owned = w.owned + w.covering > 0 ? `${w.owned} owned${w.covering ? ` · ${w.covering} covering` : ""}` : "no assignments";
  const f = flagOf(r, ctx);
  const sign = !r.upn ? "No IDC account"
    : r.last_seen_at ? (r.last_role ? `Signed in ${isMe(r, ctx) ? "now" : relative(r.last_seen_at, ctx.now).toLowerCase()} · ${ROLE_LABEL[r.last_role]}` : `Last active ${relative(r.last_seen_at, ctx.now).toLowerCase()} · Role not yet seen`)
    : f === "cant" ? null : noSignIn(ctx);
  return [sign, owned].filter(Boolean).join(" · ");
}

/**
 * AIC-815: the person reported a mismatch in My profile and an admin has not answered yet. It is a marker beside the flags, never one
 * of them: it does not hide Never signed in, Not found or left or Overloaded, and the view filter and the primary action still follow those.
 */
export const hasReport = (r: PersonRow): boolean => (r.reports?.length ?? 0) > 0;
export const REPORT_MARKER = "Mismatch reported";
/** AIC-987: a "Request mapping" from someone whose account maps to no analyst joins their reports; it has its own marker. */
export const MAPPING_MARKER = "Mapping requested";
export const isMappingRequest = (rep: { reason: string }): boolean => rep.reason === MAPPING_REQUEST_REASON;
export const reportMarker = (rep: { reason: string }): string => (isMappingRequest(rep) ? MAPPING_MARKER : REPORT_MARKER);
/** The person's report markers, each once, in the order their reports came. */
export const markersOf = (r: PersonRow): string[] => [...new Set((r.reports ?? []).map(reportMarker))];
/** Needs an admin: a flag, or an open report. */
export const needsYou = (r: PersonRow, ctx: Ctx): boolean => flagOf(r, ctx) !== null || hasReport(r);

export const VIEWS: readonly (readonly [View, string])[] = [["flagged", "Needs you"], ["all", "Everyone"], ["cant", "Never signed in"], ["unlinked", "Not linked to work"], ["gone", "Not found or left"], ["over", "Overloaded"]];
export const SORTS = [["needs", "Needs you first"], ["name", "Name A–Z"], ["work", "Most assignments"], ["last", "Last sign-in"]] as const;
export type Sort = (typeof SORTS)[number][0];
export const SOURCES: readonly (readonly [string, string])[] = [["", "All sources"], ["owner", "Data Layer owner"], ["staff", "IDC staff"], ["portal", "Signed in, not linked"]];
export const ACCESSES: readonly (readonly [string, string])[] = [["", "Any role"], ["analyst", "Analyst"], ["manager", "Manager"], ["admin", "Admin"], ["never", "Never signed in"]];
/** The Flag filter's choices: the views plus "No flag"; "all" is Any or none. */
export const FLAGS: readonly (readonly [string, string])[] = [["all", "Any or none"], ["flagged", "Needs you"], ["cant", "Never signed in"], ["unlinked", "Not linked to work"], ["gone", "Not found or left"], ["over", "Overloaded"], ["none", "No flag"]];

export interface Query { q: string; view: View | "none"; source: string; access: string; sort: Sort }
export const FRESH: Query = { q: "", view: "all", source: "", access: "", sort: "needs" };

export function matches(r: PersonRow, f: Query, ctx: Ctx): boolean {
  const flag = flagOf(r, ctx);
  if (f.view === "flagged" ? !needsYou(r, ctx) : f.view === "none" ? needsYou(r, ctx) : f.view !== "all" && flag !== f.view) return false;
  if (f.source && sourceOf(r) !== f.source) return false;
  if (f.access && (f.access === "never" ? !(r.upn && !r.last_seen_at) : r.last_role !== f.access || !r.last_seen_at)) return false;
  return !f.q || `${nameOf(r)} ${r.upn ?? ""}`.toLowerCase().includes(f.q.trim().toLowerCase());
}

const ORDER: Record<Flag, number> = { cant: 0, gone: 1, unlinked: 2, over: 3 };
const byName = (a: PersonRow, b: PersonRow) => nameOf(a).localeCompare(nameOf(b));
export function sorted(list: readonly PersonRow[], sort: Sort, ctx: Ctx): PersonRow[] {
  const total = (r: PersonRow) => r.work.owned + r.work.covering;
  const last = (r: PersonRow) => (r.last_seen_at ? Date.parse(r.last_seen_at) : 0);
  return [...list].sort((a, b) => {
    if (sort === "name") return byName(a, b);
    if (sort === "work") return total(b) - total(a) || byName(a, b);
    if (sort === "last") return last(b) - last(a) || byName(a, b);
    const fa = flagOf(a, ctx), fb = flagOf(b, ctx);
    // Reports first (an admin owes the person an answer), then the flags in their order.
    return (hasReport(a) ? -1 : fa ? ORDER[fa] : 9) - (hasReport(b) ? -1 : fb ? ORDER[fb] : 9) || b.work.needs_action - a.work.needs_action || byName(a, b);
  });
}

export function countOf(rows: readonly PersonRow[], view: View, ctx: Ctx): number {
  return view === "all" ? rows.length : rows.filter((r) => (view === "flagged" ? needsYou(r, ctx) : flagOf(r, ctx) === view)).length;
}

/** The people who report to `r` (their manager_upn is this person's UPN). */
export const reportsOf = (rows: readonly PersonRow[], r: PersonRow): PersonRow[] => (r.upn ? rows.filter((x) => x.manager_upn !== null && x.manager_upn.toLowerCase() === r.upn!.toLowerCase()) : []);
/** The manager as shown: their roster name, else a name the server found for their address, else the address itself (null when none is recorded). */
export const managerText = (rows: readonly PersonRow[], r: PersonRow): string | null => {
  if (!r.manager_upn) return null;
  const m = managerOf(rows, r);
  return m && nameOf(m) !== m.upn ? nameOf(m) : r.manager_name ?? r.manager_upn;
};
export const managerOf = (rows: readonly PersonRow[], r: PersonRow): PersonRow | null => (r.manager_upn ? rows.find((x) => x.upn !== null && x.upn.toLowerCase() === r.manager_upn!.toLowerCase()) ?? null : null);

export const viewTitle = (v: View | "none"): string => (v === "all" ? "Everyone" : v === "flagged" ? "People who need you" : v === "none" ? "People with no flag" : FLAG_LABEL[v]);
